import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import ArticleSingleLayout from "@/layouts/ArticleSingleLayout";
import { getArticleBySlug } from "@/utils/getArticleBySlugSimple";
import SimpleErrorBoundary from "@/components/ErrorBoundary/SimpleErrorBoundary";

const ArticleSinglePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!slug) return; // wait until React Router provides a valid slug

    const loadArticle = async () => {
      setLoading(true);
      setError(null);

      try {
        const result = await getArticleBySlug(slug);

        if (!result) {
          setError("Article not found");
          return;
        }

        if (!result.frontmatter.title) {
          console.warn("Article missing title in frontmatter");
        }

        console.log(
          `📖 Loaded article: ${result.frontmatter.title} (${result.readingTime} min read)`
        );
        setArticle(result);
      } catch (error) {
        console.error("❌ Error loading article:", error);
        setError(error.message || "Failed to load article");
      } finally {
        setLoading(false);
      }
    };

    loadArticle();
  }, [slug]); // important: depend on slug

  if (loading) {
    return (
      <div className="loading-container">
        <p>Loading article...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <h2>Oops! Something went wrong</h2>
        <p>{error}</p>
        <button onClick={() => navigate("/articles")}>
          ← Back to Articles
        </button>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="not-found-container">
        <h2>Article Not Found</h2>
        <p>The article you're looking for doesn't exist.</p>
        <button onClick={() => navigate("/articles")}>
          ← Back to Articles
        </button>
      </div>
    );
  }

  const { frontmatter, Content, readingTime } = article;

  // Generate fallback description from content if not in frontmatter
  const getDescription = () => {
    if (frontmatter.description) {
      return frontmatter.description;
    }
    if (frontmatter.excerpt) {
      return frontmatter.excerpt;
    }
    // Fallback description
    return `Read "${frontmatter.title}" - An insightful article${
      frontmatter.tags
        ? ` about ${frontmatter.tags.slice(0, 2).join(" and ")}`
        : ""
    }.`;
  };

  // Get image from frontmatter or use default
  const getImage = () => {
    if (frontmatter.image) {
      return frontmatter.image;
    }
    if (frontmatter.featuredImage) {
      return frontmatter.featuredImage;
    }
    if (frontmatter.hero?.image) {
      return frontmatter.hero.image;
    }
    // Return null if no image found - component will handle fallback
    return null;
  };

  // Add debugging before return
  console.log("🔍 Content component:", Content);
  console.log("🔍 Content type:", typeof Content);
  console.log("🔍 Description:", getDescription());
  console.log("🔍 Image:", getImage());

  return (
    <SimpleErrorBoundary>
      <ArticleSingleLayout
        heroProps={{ title: frontmatter.title }}
        title={frontmatter.title}
        description={getDescription()}
        image={getImage()}
        slug={slug}
        author={frontmatter.author}
        tags={frontmatter.tags}
        readingTime={readingTime}
        // SEO props
        seoTitle={frontmatter.seoTitle}
        seoDescription={frontmatter.seoDescription}
        seoImage={frontmatter.seoImage}
        seoImageAlt={frontmatter.seoImageAlt}
      >
        <Content />
      </ArticleSingleLayout>
    </SimpleErrorBoundary>
  );
};

export default ArticleSinglePage;
